import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';
import { Users, BookOpen, Trophy, TrendingUp, Clock, Download } from 'lucide-react';
import { apiClient } from '@/services/api';
import type { TeacherDashboardOverview } from '@/types';
import { formatDurationFromMinutes } from '@/lib/timeFormat';

interface TopPerformer {
  id: string;
  name: string;
  achievementsCount: number;
  totalPlayingHours: number;
}

interface RecentActivityItem {
  student: string;
  action: string;
  game: string;
  time: string;
}

export function DashboardView() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<TeacherDashboardOverview | null>(null);
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>([]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [overviewResponse, studentsResponse] = await Promise.all([
          apiClient.getTeacherDashboardOverview(),
          apiClient.getAllStudents(),
        ]);

        if (overviewResponse.success && overviewResponse.data) {
          setOverview(overviewResponse.data);
        } else {
          throw new Error(overviewResponse.error || 'Failed to load dashboard overview');
        }

        if (studentsResponse.success && studentsResponse.data) {
          const ranked = studentsResponse.data
            .map((student: any) => ({
              id: student.id,
              name: `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() || student.email,
              achievementsCount: student.achievementsCount ?? 0,
              totalPlayingHours: student.totalPlayingHours ?? 0,
            }))
            .filter((student: TopPerformer) => student.achievementsCount > 0)
            .sort((a: TopPerformer, b: TopPerformer) =>
              b.achievementsCount - a.achievementsCount || b.totalPlayingHours - a.totalPlayingHours
            )
            .slice(0, 5);
          setTopPerformers(ranked);
        }
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }

      // Recent activity is optional and not available on every backend.
      // Fetch it silently so a missing endpoint never surfaces as an error.
      try {
        const activityResponse = await apiClient.getRecentActivity();
        if (activityResponse.success && Array.isArray(activityResponse.data)) {
          setRecentActivity(activityResponse.data);
        }
      } catch {
        // Ignore: leave recent activity empty when unsupported.
      }
    };
    loadData();
  }, []);

  const stats = [
    {
      title: 'Total Students',
      value: overview?.totalStudents ?? 0,
      icon: Users,
      description: 'Enrolled in your units',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Games Assigned',
      value: overview?.totalGamesAssigned ?? 0,
      icon: BookOpen,
      description: 'Across all units',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Total Games',
      value: overview?.totalGames ?? 0,
      icon: TrendingUp,
      description: 'Available games',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Achievements Unlocked',
      value: overview?.totalAchievementsUnlocked ?? 0,
      icon: Trophy,
      description: 'By all students',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      title: 'Total Playing Time',
      value: formatDurationFromMinutes(overview?.totalPlayingMinutes ?? 0),
      icon: Clock,
      description: 'Across all units and games',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
    {
      title: 'Total Installs',
      value: overview?.totalInstallCount ?? 0,
      icon: Download,
      description: 'Across all games',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ];

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
        </div>
        <div className="text-center py-8 text-muted-foreground">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 sm:p-6">
                <CardTitle className="text-xs sm:text-sm font-medium">{stat.title}</CardTitle>
                <div className={`p-1.5 sm:p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-3 w-3 sm:h-4 sm:w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="text-xl sm:text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{stat.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest student activities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No recent activity</p>
              ) : (
                recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-[hsl(var(--color-primary-text))]">
                        {activity.student.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <span className="font-medium">{activity.student}</span>{' '}
                        <span className="text-muted-foreground">{activity.action}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {activity.game} • {activity.time}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Top Performers</CardTitle>
            <CardDescription>Students with most achievements</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topPerformers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No data available</p>
              ) : (
                topPerformers.map((performer, index) => (
                  <div key={performer.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-[hsl(var(--color-primary-text))]">#{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{performer.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {performer.totalPlayingHours}h played
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Trophy className="h-3.5 w-3.5 text-yellow-600" />
                      <span className="text-sm font-semibold">{performer.achievementsCount}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

